import { ReactNode, useState } from "react";
import { Box, Combobox, InputBase, Text, useCombobox } from "@mantine/core";
import filterClasses from "./DiscoverQuickFilters.module.css";

const FILTER_CLEAR = "__discover_filter_clear__";

export type DiscoverPickOption = { value: string; label: string };

export function DiscoverPickCombobox({
  placeholder,
  leftSection,
  value,
  onChange,
  options,
  getOptionDetail,
  disabled,
}: {
  placeholder: string;
  leftSection?: ReactNode;
  value: string | null;
  onChange: (next: string | null) => void;
  options: DiscoverPickOption[];
  getOptionDetail?: (value: string) => string | null | undefined;
  disabled?: boolean;
}) {
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setHoverValue(null);
    },
  });
  const [hoverValue, setHoverValue] = useState<string | null>(null);

  const withClear: DiscoverPickOption[] = [
    { value: FILTER_CLEAR, label: placeholder.replace(/^Pick\s+/i, "Any ") },
    ...options,
  ];

  const displayLabel =
    value != null ? options.find((o) => o.value === value)?.label ?? value : null;

  const comboboxOptions = withClear.map((item, index) => (
    (() => {
      const detail = item.value !== FILTER_CLEAR ? getOptionDetail?.(item.value) : null;
      const showDetail = hoverValue === item.value && Boolean(detail);
      const detailText = typeof detail === "string" ? detail : "";
      const detailParts = detailText.split(": ");
      const hasCountriesList = detailParts.length > 1;

      return (
    <Combobox.Option
      value={item.value}
      key={item.value}
      className={filterClasses.option}
      onMouseOver={() => {
        combobox.selectOption(index);
        setHoverValue(item.value);
      }}
      onMouseLeave={() => setHoverValue((prev) => (prev === item.value ? null : prev))}
    >
      <Box>
        <Text size="sm" fw={600} className={filterClasses.optionLabel}>
          {item.label}
        </Text>
        {showDetail ? (
          <Text size="xs" mt={4} className={filterClasses.optionDetail}>
            {hasCountriesList ? (
              <>
                {detailParts[0]}:{" "}
                <Text span fw={600} className={filterClasses.optionDetailStrong}>
                  {detailParts.slice(1).join(": ")}
                </Text>
              </>
            ) : (
              detail
            )}
          </Text>
        ) : null}
      </Box>
    </Combobox.Option>
      );
    })()
  ));

  return (
    <Box w="100%" miw={0}>
      <Combobox
        store={combobox}
        withinPortal={false}
        classNames={{ dropdown: filterClasses.dropdown }}
        onOptionSubmit={(val) => {
          onChange(val === FILTER_CLEAR ? null : val);
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <InputBase
            component="button"
            type="button"
            pointer
            leftSection={leftSection}
            leftSectionPointerEvents="none"
            rightSection={<Combobox.Chevron />}
            onClick={() => !disabled && combobox.toggleDropdown()}
            rightSectionPointerEvents="none"
            disabled={disabled}
            size="md"
            radius="md"
            styles={{
              wrapper: {
                flex: 1,
                minWidth: 0,
                maxWidth: "100%",
                alignItems: "center",
              },
              section: { flexShrink: 0, alignSelf: "center" },
              input: {
                flex: 1,
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textAlign: "left",
                lineHeight: 1.35,
              },
            }}
          >
            {displayLabel ? (
              <Text
                component="span"
                size="sm"
                inherit
                title={displayLabel}
                style={{
                  display: "block",
                  width: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {displayLabel}
              </Text>
            ) : (
              <Text
                component="span"
                size="sm"
                c="dimmed"
                inherit
                title={placeholder}
                style={{
                  display: "block",
                  width: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {placeholder}
              </Text>
            )}
          </InputBase>
        </Combobox.Target>

        <Combobox.Dropdown onMouseLeave={() => combobox.resetSelectedOption()}>
          <Combobox.Options mah={280} style={{ overflowY: "auto" }}>
            {comboboxOptions}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Box>
  );
}
